import { z } from "zod";

export interface ApplicationConfig {
  backendUrl: string;
  clientSideGraphNodeLimit: number;
  cognitoClientId: string;
  cognitoDomainName: string;
  cognitoUserPoolId: string;
  envMode: string;
  sentryDsn: string;
}

export const ApplicationConfigSchema = z.object({
  backendUrl: z.string(),
  clientSideGraphNodeLimit: z.number(),
  cognitoClientId: z.string(),
  cognitoDomainName: z.string(),
  cognitoUserPoolId: z.string(),
  envMode: z.string(),
  sentryDsn: z.string(),
});

declare global {
  interface Window {
    ENV?: {
      BACKEND_URL: string;
      CLIENT_SIDE_GRAPH_NODE_LIMIT: string;
      COGNITO_CLIENT_ID: string;
      COGNITO_DOMAIN_NAME: string;
      COGNITO_USER_POOL_ID: string;
      ENV_MODE: string;
      SENTRY_DSN: string;
    };
  }
}

export const getAppConfig = (): ApplicationConfig => {
  const isDev = import.meta.env.MODE === "development";
  const useWindowEnv = !isDev && typeof window !== "undefined" && window.ENV;

  const config = {
    backendUrl: useWindowEnv
      ? (window.ENV?.BACKEND_URL ?? "")
      : import.meta.env.VITE_BACKEND_URL,
    clientSideGraphNodeLimit: parseInt(
      useWindowEnv
        ? (window.ENV?.CLIENT_SIDE_GRAPH_NODE_LIMIT ?? "100")
        : (import.meta.env.VITE_CLIENT_SIDE_GRAPH_NODE_LIMIT ?? "100"),
      10,
    ),
    cognitoClientId: useWindowEnv
      ? (window.ENV?.COGNITO_CLIENT_ID ?? "")
      : import.meta.env.VITE_COGNITO_CLIENT_ID,
    cognitoDomainName: useWindowEnv
      ? (window.ENV?.COGNITO_DOMAIN_NAME ?? "")
      : import.meta.env.VITE_COGNITO_DOMAIN_NAME,
    cognitoUserPoolId: useWindowEnv
      ? (window.ENV?.COGNITO_USER_POOL_ID ?? "")
      : import.meta.env.VITE_COGNITO_USER_POOL_ID,
    envMode: useWindowEnv
      ? (window.ENV?.ENV_MODE ?? "production")
      : (import.meta.env.MODE ?? "production"),
    sentryDsn: useWindowEnv
      ? window.ENV?.SENTRY_DSN
      : import.meta.env.VITE_SENTRY_DSN,
  };

  try {
    // Parse and validate the config
    return ApplicationConfigSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(
        `[Application Config]: Environments validation failed. Please check environment variables.
        Error message: ${error.message}`,
      );
    }
    throw error;
  }
};
